import React, { useState } from 'react';
import { useWeather } from '../../context';
import { Ship, Anchor, AlertTriangle, Wind } from 'lucide-react';
import { vesselTypes, coverageTypes } from '../../constants/parameters';

export function ShippingPanel() {
  const { weatherData } = useWeather();
  const [activeTab, setActiveTab] = useState<'vessels' | 'risks' | 'coverage'>('vessels');
  const [selectedVessel, setSelectedVessel] = useState<string | null>(null);

  // Mock data for demonstration
  const vessels = [
    {
      id: 'v1',
      name: 'Pacific Voyager',
      type: 'container',
      status: 'en_route',
      position: { lat: 25.7617, lon: -80.1918 },
      destination: 'Miami Port',
      eta: '2024-01-15T14:30:00Z'
    },
    {
      id: 'v2',
      name: 'Atlantic Pioneer',
      type: 'bulk',
      status: 'loading',
      position: { lat: 40.7128, lon: -74.0060 },
      destination: 'New York Harbor',
      eta: '2024-01-16T09:00:00Z'
    }
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Tab Navigation */}
      <div className="flex border-b border-[var(--border-color)]">
        <button
          className={`px-4 py-2 ${activeTab === 'vessels' ? 'text-[var(--accent-color)] border-b-2 border-[var(--accent-color)]' : 'text-[var(--secondary-text)]'}`}
          onClick={() => setActiveTab('vessels')}
        >
          <Ship className="w-4 h-4 inline mr-2" />
          Vessels
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'risks' ? 'text-[var(--accent-color)] border-b-2 border-[var(--accent-color)]' : 'text-[var(--secondary-text)]'}`}
          onClick={() => setActiveTab('risks')}
        >
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          Risks
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'coverage' ? 'text-[var(--accent-color)] border-b-2 border-[var(--accent-color)]' : 'text-[var(--secondary-text)]'}`}
          onClick={() => setActiveTab('coverage')}
        >
          <Wind className="w-4 h-4 inline mr-2" />
          Coverage
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'vessels' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Active Vessels</h3>
            {vessels.map(vessel => (
              <div
                key={vessel.id}
                className={`glass-panel p-4 cursor-pointer transition-all ${
                  selectedVessel === vessel.id ? 'ring-2 ring-[var(--accent-color)]' : ''
                }`}
                onClick={() => setSelectedVessel(vessel.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{vessel.name}</h4>
                    <p className="text-sm text-[var(--secondary-text)]">
                      {vesselTypes.find(t => t.value === vessel.type)?.text}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${vessel.status === 'en_route' ? 'text-[var(--success-color)]' : 'text-[var(--warning-color)]'}`}>
                      {vessel.status === 'en_route' ? 'En Route' : 'Loading'}
                    </p>
                    <p className="text-sm text-[var(--secondary-text)]">
                      ETA: {new Date(vessel.eta).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {selectedVessel === vessel.id && (
                  <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[var(--secondary-text)]">Position</p>
                        <p className="text-sm">
                          {vessel.position.lat.toFixed(4)}°, {vessel.position.lon.toFixed(4)}°
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-[var(--secondary-text)]">Destination</p>
                        <p className="text-sm">{vessel.destination}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'risks' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Risk Assessment</h3>
            {weatherData?.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="glass-panel p-4">
                  <h4 className="font-semibold mb-2">Storm Risk</h4>
                  <div className="text-3xl font-bold text-[var(--warning-color)]">
                    75
                  </div>
                  <p className="text-sm text-[var(--secondary-text)]">
                    High risk due to tropical cyclone activity
                  </p>
                </div>
                <div className="glass-panel p-4">
                  <h4 className="font-semibold mb-2">Credit Risk</h4>
                  <div className="text-3xl font-bold text-[var(--success-color)]">
                    15
                  </div>
                  <p className="text-sm text-[var(--secondary-text)]">
                    Low counterparty risk
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[var(--secondary-text)]">
                No risk data available. Please fetch weather data first.
              </p>
            )}
          </div>
        )}

        {activeTab === 'coverage' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Coverage Structure</h3>
            <div className="glass-panel p-4">
              <div className="space-y-2">
                {coverageTypes.map(type => (
                  <div key={type.value} className="flex justify-between items-center">
                    <span>{type.text}</span>
                    <span className="text-[var(--accent-color)]">
                      ${type.value === 'parametric' ? '5M' : type.value === 'trade_credit' ? '2M' : '3M'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <h4 className="font-semibold mb-2">Trigger Conditions</h4>
                <div className="space-y-2 text-sm">
                  <p>• Wind speed exceeds 100 mph</p>
                  <p>• Storm within 100 nautical miles</p>
                  <p>• Category 3+ hurricane</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}